/**
 * Google Drive file picker.
 *
 * Turns a plain URL field into one the member can fill from their own Drive. What lands in the
 * field is the Drive REST media URL of the picked file — an address, not a copy: the file stays
 * where it is, and the platform relays it through its own image proxy using the delegation the
 * member granted. Revoking that connection is enough to cut the link.
 *
 * The container carries the wiring (`.ui-google-picker` with its data attributes) and each
 * field inside it is a `.ui-input-google-picker`. The server renders that container only when the
 * member actually has an active Drive connection, so the mere presence of a button means the pick
 * can succeed. Where it is absent, the field stays a normal URL input — pasting a public share
 * link keeps working, and always will.
 *
 * Nothing is fetched until a button is clicked: no access token is requested for a page the member
 * only reads, and the token in hand is always fresh rather than one minted at page load and stale
 * by the time it is used.
 */

import { logger } from '../../core/base.js';

const GOOGLE_API_SCRIPT = 'https://apis.google.com/js/api.js';

/** The bytes of a Drive file, addressed through the REST API — what the image proxy will fetch. */
const driveMediaUrl = fileId => `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;

/** Loading of Google's script is shared by every button on the page, and attempted once. */
let pickerApiPromise = null;

function loadPickerApi() {
    if (pickerApiPromise) {
        return pickerApiPromise;
    }

    pickerApiPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = GOOGLE_API_SCRIPT;
        script.async = true;
        script.defer = true;
        script.onload = () => gapi.load('picker', { callback: resolve, onerror: reject });
        script.onerror = () => reject(new Error(`Unable to load ${GOOGLE_API_SCRIPT}`));
        document.body.appendChild(script);
    });

    return pickerApiPromise;
}

/**
 * A short-lived access token for the member's Drive connection, read from the owner-gated
 * credentials endpoint. It never appears in the HTML — the page only knows the URL to ask.
 */
async function fetchAccessToken(credentialsUrl) {
    const response = await fetch(credentialsUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
    });

    if (!response.ok) {
        throw new Error(`Credentials endpoint answered ${response.status} ${response.statusText}`);
    }

    const { accessToken } = await response.json();
    if (!accessToken) {
        throw new Error('Credentials response carries no accessToken');
    }
    return accessToken;
}

function buildPicker({ accessToken, appId, developerKey, onPicked }) {
    // Images only: these fields feed an avatar and a banner, and the proxy decodes rasters. Offering
    // a spreadsheet would only let the member choose something that can never render.
    const view = new google.picker.DocsView(google.picker.ViewId.DOCS_IMAGES);
    view.setIncludeFolders(true);

    // Google's default box is 1051x650 and it never checks whether that fits. On a laptop in a
    // split window the browse pane ends up cropped, and the theme can only clip what overflows —
    // the layout inside the iframe is Google's. Giving it the room it actually has is the only way
    // it lays itself out correctly; the floors keep the dialog usable on a phone.
    const width = Math.max(320, Math.min(1051, window.innerWidth - 64));
    const height = Math.max(400, Math.min(650, window.innerHeight - 96));

    return new google.picker.PickerBuilder()
        .addView(view)
        .setSize(width, height)
        .setOAuthToken(accessToken)
        // The app id is the Cloud project NUMBER, not the OAuth2 client id. With the drive.file
        // scope it is what grants this app access to the picked file — get it wrong and the pick
        // succeeds while every later read is denied.
        .setAppId(appId)
        .setDeveloperKey(developerKey)
        .setOrigin(`${window.location.protocol}//${window.location.host}`)
        .setCallback(data => {
            if (data[google.picker.Response.ACTION] !== google.picker.Action.PICKED) {
                return;
            }
            const picked = data[google.picker.Response.DOCUMENTS][0];
            if (picked) {
                onPicked(picked[google.picker.Document.ID]);
            }
        })
        .build();
}

function initField(container, field) {
    const input = field.querySelector('input');
    const button = field.querySelector('button');

    if (!input || !button) {
        return;
    }

    button.addEventListener('click', async () => {
        button.disabled = true;
        button.classList.add('is-loading');

        try {
            const [accessToken] = await Promise.all([
                fetchAccessToken(container.dataset.credentialsSrc),
                loadPickerApi()
            ]);

            buildPicker({
                accessToken,
                appId: container.dataset.appId,
                developerKey: container.dataset.developerKey,
                onPicked: fileId => {
                    input.value = driveMediaUrl(fileId);
                    // Let the rest of the form react as if the member had typed it — validation and
                    // any preview listen for these, and a silent assignment fires neither.
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }).setVisible(true);
        } catch (error) {
            logger.error('Google Picker unavailable:', error);
            button.classList.add('tone-danger');
        } finally {
            button.disabled = false;
            button.classList.remove('is-loading');
        }
    });
}

function initGooglePicker() {
    document.querySelectorAll('.ui-google-picker').forEach(container => {
        container.querySelectorAll('.ui-input-google-picker')
            .forEach(field => initField(container, field));
    });
}

document.addEventListener('DOMContentLoaded', initGooglePicker);
